import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { OptionHistoryPoint, OptionHistoryMetricKey } from '../types/StockData';

interface OptionHistoryChartProps {
  data: OptionHistoryPoint[];
  selectedMetric: OptionHistoryMetricKey;
  optionContract: string;
  isLoading: boolean;
}

export const OptionHistoryChart: React.FC<OptionHistoryChartProps> = ({
  data,
  selectedMetric,
  optionContract,
  isLoading,
}) => {
  const [normalizeMetric, setNormalizeMetric] = useState<boolean>(false);

  const metricLabels: Record<OptionHistoryMetricKey, string> = {
    volume: 'Volume',
    implied_volatility: 'Implied Volatility',
    bid_volume: 'Bid Volume',
    ask_volume: 'Ask Volume',
    mid_volume: 'Mid Volume',
    floor_volume: 'Floor Volume',
    sweep_volume: 'Sweep Volume',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatYAxis = (value: number) => {
    if (normalizeMetric) {
      return `${value.toFixed(2)}%`;
    }
    if (selectedMetric === 'implied_volatility') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (Math.abs(value) >= 1e6) {
      return `${(value / 1e6).toFixed(1)}M`;
    }
    if (Math.abs(value) >= 1e3) {
      return `${(value / 1e3).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) => {
    if (normalizeMetric) {
      return `${value.toFixed(2)}%`;
    }
    if (selectedMetric === 'implied_volatility') {
      return `${(value * 100).toFixed(2)}%`;
    }
    return value.toLocaleString();
  };

  // Sort data by date ascending for proper chart display (before early returns)
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data]);

  // Calculate normalized data
  const displayData = useMemo(() => {
    if (!normalizeMetric || sortedData.length === 0) {
      return sortedData.map(d => ({
        ...d,
        metricDisplay: d[selectedMetric] as number,
      }));
    }

    // Find first non-zero value as baseline
    const values = sortedData.map(d => d[selectedMetric] as number).filter(v => v !== null && v !== undefined && isFinite(v));
    const baseVal = values.find(v => v !== 0) ?? values[0] ?? 1;
    const safeBase = baseVal || 1;

    return sortedData.map(d => {
      const raw = d[selectedMetric] as number;
      const normalized = ((raw / safeBase) - 1) * 100;
      return {
        ...d,
        metricDisplay: normalized,
      };
    });
  }, [sortedData, selectedMetric, normalizeMetric]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading chart data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <svg
              className="w-16 h-16 text-gray-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-gray-600 text-lg font-medium">No data available</p>
            <p className="text-gray-500 text-sm mt-2">
              Enter an option contract to view historical data
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {metricLabels[selectedMetric]} History
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Contract: <span className="font-mono font-medium">{optionContract}</span>
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={normalizeMetric}
            onChange={(e) => setNormalizeMetric(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Normalize metric
        </label>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={displayData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            tickFormatter={formatYAxis}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px',
            }}
            labelFormatter={(label) => `Date: ${formatDate(label)}`}
            formatter={(value: number) => [formatTooltipValue(value), metricLabels[selectedMetric]]}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="metricDisplay"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ fill: '#2563eb', r: 4 }}
            activeDot={{ r: 6 }}
            name={metricLabels[selectedMetric]}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
